<script setup>
/**
 * Ikonene i grensesnittet.
 *
 * Tegnet som strek på et rutenett på 24×24, med samme strektykkelse og runde
 * hjørner overalt. Det er dét som gjør at et sett med ikoner ser ut som ett
 * sett: emoji kommer fra hver sin skrift, i hver sin vekt og hver sin
 * fargeskala, og et grensesnitt bygget av dem ser sammenrasket ut uansett hvor
 * godt hvert enkelt symbol passer.
 *
 * Ikonene arver farge fra teksten rundt (currentColor), så en knapp, et kort
 * eller en fane fargelegger ikonet sitt ved å sette color - ingen egne
 * varianter per flate.
 *
 * Emoji er ikke borte fra appen, men de er flyttet dit de hører hjemme: der de
 * ER innholdet. Barnet velger 🦊 som avatar, forelderen velger 🧹 til
 * kategorien «Rydding», og et kjøp føres under 🍭. Det er valg familien har
 * tatt, ikke knapper vi har merket.
 */
import { computed } from 'vue'

const props = defineProps({
  name: { type: String, required: true },
  /** Piksler. Følger som regel teksten rundt; 20 er standard i lister. */
  size: { type: [Number, String], default: 20 },
  /**
   * Tekst for skjermlesere. Uten den regnes ikonet som pynt og skjules - som
   * er riktig når det står ved siden av en etikett som sier det samme.
   */
  label: { type: String, default: '' },
})

/** Sirkel som path, så alle ikoner kan beskrives med samme liste. */
const circle = (cx, cy, r) =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0`

const STAR = 'M12 3.6l2.62 5.31 5.86.85-4.24 4.13 1 5.84L12 16.98l-5.24 2.75 '
  + '1-5.84-4.24-4.13 5.86-.85z'

const ICONS = {
  // --- Navigasjon -----------------------------------------------------------
  home: [
    'M3.4 10.2 12 3.5l8.6 6.7',
    'M5.6 8.9V19a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6V8.9',
    'M9.6 20.6v-5.4a1 1 0 0 1 1-1h2.8a1 1 0 0 1 1 1v5.4',
  ],
  tasks: [
    'M3.5 6.3 4.9 7.7 7.7 4.9',
    'M3.5 12.3 4.9 13.7 7.7 10.9',
    'M3.5 18.3 4.9 19.7 7.7 16.9',
    'M11 6.4h9.5',
    'M11 12.4h9.5',
    'M11 18.4h9.5',
  ],
  trophy: [
    'M7.2 4h9.6v4.6a4.8 4.8 0 0 1-9.6 0z',
    'M7.2 5.6H4.9a.9.9 0 0 0-.9.9v.6a3.7 3.7 0 0 0 3.7 3.7',
    'M16.8 5.6h2.3a.9.9 0 0 1 .9.9v.6a3.7 3.7 0 0 1-3.7 3.7',
    'M12 13.4v3.4',
    'M8.4 20.5h7.2l-.5-1.9a1.1 1.1 0 0 0-1.1-.8h-4a1.1 1.1 0 0 0-1.1.8z',
  ],
  users: [
    'M15.6 20.5v-1.7a3.6 3.6 0 0 0-3.6-3.6H6.6A3.6 3.6 0 0 0 3 18.8v1.7',
    circle(9.3, 7.6, 3.6),
    'M20.9 20.5v-1.7a3.6 3.6 0 0 0-2.7-3.5',
    'M15.8 4.2a3.6 3.6 0 0 1 0 6.9',
  ],
  wallet: [
    'M3.5 8.4a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v9.2a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z',
    'M3.6 9V7.1a1.6 1.6 0 0 1 1.3-1.57l9.7-1.9A1.4 1.4 0 0 1 16.3 5v1.4',
    circle(16.6, 13, 1.2),
  ],
  receipt: [
    'M5.8 3.5h12.4v17l-2.07-1.4-2.07 1.4L12 19.1l-2.07 1.4-2.06-1.4L5.8 20.5z',
    'M9 8.2h6',
    'M9 12.2h6',
  ],

  // --- Penger ---------------------------------------------------------------
  deposit: [circle(12, 12, 8.6), 'M12 7.9v8.2', 'M8.6 12.7 12 16.1l3.4-3.4'],
  withdraw: [circle(12, 12, 8.6), 'M12 16.1V7.9', 'M8.6 11.3 12 7.9l3.4 3.4'],
  cart: [
    'M2.6 3.6h2.2l2.55 11.6a1.6 1.6 0 0 0 1.56 1.25h7.9a1.6 1.6 0 0 0 1.56-1.22'
      + 'l1.44-5.98H5.7',
    circle(9.8, 20, 1.3),
    circle(17.4, 20, 1.3),
  ],
  pencil: ['M15.9 3.9a2.1 2.1 0 0 1 2.97 2.97L7.7 18.05l-3.95 1 1-3.95z', 'M14.3 5.5 18.4 9.6'],

  // --- Stjerner -------------------------------------------------------------
  star: [STAR],
  'star-filled': [STAR],
  crown: ['M3.4 7.4 7 11.2 12 4.8l5 6.4 3.6-3.8L18.9 17H5.1z', 'M5.6 20.4h12.8'],
  sparkles: [
    'M11 3.4l1.75 4.85L17.6 10l-4.85 1.75L11 16.6 9.25 11.75 4.4 10l4.85-1.75z',
    'M18 14.4l.85 2.35 2.35.85-2.35.85L18 20.8l-.85-2.35-2.35-.85 2.35-.85z',
  ],
  // Krøllen nede til venstre er det som skiller en flamme fra en vanndråpe.
  flame: [
    'M12 3c.5 2.7 2.1 4.5 3.7 5.9 1.8 1.6 2.8 3.4 2.8 5.4a6.5 6.5 0 0 1-13 0c0-1.2.4-2.3 '
      + '1.1-3.2a2.6 2.6 0 0 0 2.6 2.6A2.6 2.6 0 0 0 11.8 11c0-1.3-.5-2-1-2.9-1-2-.2-3.7 '
      + '1.2-5.1z',
  ],

  // --- Handlinger og status -------------------------------------------------
  plus: ['M12 4.8v14.4', 'M4.8 12h14.4'],
  'arrow-left': ['M19.4 12H4.6', 'M11 5.6 4.6 12l6.4 6.4'],
  close: ['M6.2 6.2 17.8 17.8', 'M17.8 6.2 6.2 17.8'],
  check: ['M4.6 12.6 9.4 17.4 19.4 6.6'],
  'check-circle': [circle(12, 12, 8.6), 'M8.3 12.2 10.9 14.8 15.9 9.2'],
  clock: [circle(12, 12, 8.6), 'M12 6.9v5.4l3.4 2'],
  // Faste lommepenger: en dato som kommer igjen, ikke et klokkeslett.
  calendar: [
    'M4.4 7.6a2 2 0 0 1 2-2h11.2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6.4a2 2 0 0 1-2-2z',
    'M4.4 10.4h15.2',
    'M8.6 3.9v3.4',
    'M15.4 3.9v3.4',
  ],
  alert: [
    'M10.6 4.6a1.6 1.6 0 0 1 2.8 0l7.4 13.1a1.6 1.6 0 0 1-1.4 2.4H4.6a1.6 1.6 0 0 1-1.4-2.4z',
    'M12 9.7v4.1',
    'M12 17.3h.01',
  ],
  settings: [
    'M3.8 7.1h6.4',
    'M15.4 7.1h4.8',
    circle(12.8, 7.1, 2.1),
    'M3.8 16.9h4.8',
    'M14 16.9h6.2',
    circle(11.4, 16.9, 2.1),
  ],
  copy: [
    'M9.4 9.4a2 2 0 0 1 2-2h7.1a2 2 0 0 1 2 2v7.1a2 2 0 0 1-2 2h-7.1a2 2 0 0 1-2-2z',
    'M16.1 7.4V5.5a2 2 0 0 0-2-2H5.5a2 2 0 0 0-2 2v8.6a2 2 0 0 0 2 2h1.9',
  ],
  eye: [
    'M2.4 12S6.3 5.6 12 5.6 21.6 12 21.6 12 17.7 18.4 12 18.4 2.4 12 2.4 12z',
    circle(12, 12, 3),
  ],
  'eye-off': [
    'M4 4 20 20',
    'M10.1 6a9.6 9.6 0 0 1 1.9-.2c5.7 0 9.6 6.2 9.6 6.2a17.6 17.6 0 0 1-2.9 3.6',
    'M6.6 7.7A17.3 17.3 0 0 0 2.4 12s3.9 6.4 9.6 6.4a9.4 9.4 0 0 0 3.4-.63',
    'M9.9 10a3 3 0 0 0 4.2 4.2',
  ],
  globe: [
    circle(12, 12, 8.6),
    'M3.6 9.4h16.8',
    'M3.6 14.6h16.8',
    'M12 3.4c-4.6 5.2-4.6 12 0 17.2 4.6-5.2 4.6-12 0-17.2z',
  ],
  mail: [
    'M3.4 7.4a2 2 0 0 1 2-2h13.2a2 2 0 0 1 2 2v9.2a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2z',
    'M3.8 7.9 12 13.2l8.2-5.3',
  ],
  user: [
    'M19.4 20.5v-1.9a4.4 4.4 0 0 0-4.4-4.4H9a4.4 4.4 0 0 0-4.4 4.4v1.9',
    circle(12, 7.6, 3.9),
  ],
  'user-plus': [
    'M13.6 20.5v-1.7a3.7 3.7 0 0 0-3.7-3.7H6.2a3.7 3.7 0 0 0-3.7 3.7v1.7',
    circle(8.05, 7.6, 3.7),
    'M18.6 8.2v5.6',
    'M21.4 11h-5.6',
  ],
  inbox: [
    'M3.5 12.6h4.2l1.3 2.4h6l1.3-2.4h4.2',
    'M6.4 4.7h11.2a1.6 1.6 0 0 1 1.46.95l2.44 5.5v6.65a2 2 0 0 1-2 2h-15.'
      + '2a2 2 0 0 1-2-2v-6.65l2.44-5.5a1.6 1.6 0 0 1 1.46-.95z',
  ],
}

/** Fylte ikoner har ingen strek - ellers ville formen blitt tykkere enn resten. */
const FILLED = new Set(['star-filled'])

const paths = computed(() => ICONS[props.name] ?? [])
const filled = computed(() => FILLED.has(props.name))
</script>

<template>
  <svg
    class="icon"
    :class="{ 'icon--filled': filled }"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    :fill="filled ? 'currentColor' : 'none'"
    :stroke="filled ? 'none' : 'currentColor'"
    stroke-width="1.75"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : undefined"
    :aria-hidden="label ? undefined : 'true'"
  >
    <title v-if="label">{{ label }}</title>
    <path v-for="(d, i) in paths" :key="i" :d="d" />
  </svg>
</template>

<style scoped>
.icon {
  display: block;
  flex: none;
  /* Ikoner står nesten alltid ved siden av tekst. Uten dette sitter de på
     grunnlinja og ser ut til å henge for lavt. */
  vertical-align: middle;
}
</style>
